// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Customer search component module providing advanced customer selection functionality.
 * Combines autocomplete search with customer creation capabilities.
 */
import { CustomerFilter, getCustomers } from '@/customer/api/get-customers';
import {
  CREATE_CUSTOMER_OPTION_ID,
  CustomerAutocomplete,
} from '@/customer/components/customer-autocomplete';
import { SparseCustomer } from '@/sparse/api/types';
import { FilterOptionsState, createFilterOptions } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { reject } from 'lodash-es';
import { ForwardedRef, forwardRef } from 'react';

/**
 * Base filter function for customer autocomplete.
 * Uses Material-UI's createFilterOptions for basic text matching.
 */
const filter = createFilterOptions<SparseCustomer>();

/**
 * Compares two customers for sorting in search results.
 * Prioritizes active customers over archived ones, then sorts alphabetically by name.
 *
 * @param customer - First customer to compare
 * @param other - Second customer to compare
 * @returns Negative if customer should come first, positive if other should come first
 */
function compareCustomers(customer: SparseCustomer, other: SparseCustomer) {
  if (customer.isArchived === other.isArchived) {
    return customer.name.localeCompare(other.name);
  }

  return customer.isArchived ? 1 : -1;
}

/**
 * Compares two customers for sorting with suppliers first.
 * Prioritizes suppliers over non-suppliers, then active over archived, then alphabetically.
 *
 * @param customer - First customer to compare
 * @param other - Second customer to compare
 * @returns Negative if customer should come first, positive if other should come first
 */
function compareCustomersSupplierFirst(
  customer: SparseCustomer,
  other: SparseCustomer
) {
  // Suppliers first
  if (customer.isSupplier !== other.isSupplier) {
    return customer.isSupplier ? -1 : 1;
  }

  // Then active over archived
  if (customer.isArchived !== other.isArchived) {
    return customer.isArchived ? 1 : -1;
  }

  // Then alphabetically
  return customer.name.localeCompare(other.name);
}

/**
 * Compares two customers for sorting with customers (non-suppliers) first.
 * Prioritizes non-suppliers over suppliers, then active over archived, then alphabetically.
 *
 * @param customer - First customer to compare
 * @param other - Second customer to compare
 * @returns Negative if customer should come first, positive if other should come first
 */
function compareCustomersCustomerFirst(
  customer: SparseCustomer,
  other: SparseCustomer
) {
  // Non-suppliers first
  if (customer.isSupplier !== other.isSupplier) {
    return customer.isSupplier ? 1 : -1;
  }

  // Then active over archived
  if (customer.isArchived !== other.isArchived) {
    return customer.isArchived ? 1 : -1;
  }

  // Then alphabetically
  return customer.name.localeCompare(other.name);
}

/**
 * Props for the CustomerSearch component
 */
interface Props {
  /**
   * Name attribute for the form input element.
   * Used for form integration and accessibility.
   */
  name?: string;

  /**
   * Label text displayed above the search input.
   * Provides context for the search field.
   */
  label?: string;

  /**
   * Controls the error state of the component.
   * When true, displays error styling and helper text in error state.
   */
  error?: boolean;

  /**
   * Text displayed below the input field.
   * Can be used for hints, validation messages, or error details.
   */
  helperText?: string;

  /**
   * Currently selected customer in the search field.
   * Null when no customer is selected.
   */
  value: SparseCustomer | null;

  /**
   * Callback fired when a customer selection changes.
   * @param value - The newly selected customer or null if selection cleared
   */
  onChange: (value: SparseCustomer | null) => void;

  /**
   * Callback fired when user chooses to create a new customer.
   * @param name - The name entered for the new customer
   */
  onCreate: (name: string) => void;

  /**
   * Optional filter to apply when fetching customers.
   * Used to filter by customer type (isSupplier, isCustomer) or archived status.
   */
  filter?: CustomerFilter;

  /**
   * Optional grouping mode for the customer list.
   * - 'supplier': Groups customers with suppliers first
   * - 'customer': Groups customers with non-suppliers first
   * - undefined: No grouping, only sorted by active/archived status
   */
  groupByMode?: 'supplier' | 'customer';

  /**
   * When true, the clear button is hidden and the value cannot be cleared.
   */
  disableClearable?: boolean;
}

/**
 * A controlled search component for selecting or creating customers.
 * Provides real-time search with customer creation capabilities.
 *
 * Features:
 * - Autocomplete search against existing customers
 * - Real-time filtering and sorting
 * - Create new customer option
 * - Active/archived customer handling
 * - Loading state management
 * - Error state support
 * - Form integration
 *
 * Search Behavior:
 * 1. Shows all active customers when empty
 * 2. Filters by name as user types
 * 3. Offers "create new" option if no exact match
 * 4. Prioritizes active customers in results
 *
 * @example
 * ```tsx
 * <CustomerSearch
 *   label="Select customer"
 *   value={selectedCustomer}
 *   onChange={handleCustomerChange}
 *   onCreate={handleCustomerCreate}
 *   error={hasError}
 *   helperText={errorMessage}
 * />
 * ```
 */
function CustomerSearchRenderer(
  props: Props,
  ref: ForwardedRef<HTMLInputElement>
) {
  // Use provided filter or default to showing all customers
  const customerFilter = props.filter ?? { isArchived: 'all' };

  // Fetch customer data with React Query
  const { data, isLoading } = useQuery({
    queryKey: ['customer', customerFilter],
    queryFn: () => getCustomers(customerFilter),
  });

  // Sort customers based on grouping mode
  // IMPORTANT: Create a copy before sorting to avoid mutating the shared React Query cache
  const sortFn =
    props.groupByMode === 'supplier'
      ? compareCustomersSupplierFirst
      : props.groupByMode === 'customer'
        ? compareCustomersCustomerFirst
        : compareCustomers;

  const customers = data?.customers.slice().sort(sortFn) ?? [];

  /**
   * Custom filter function for the autocomplete options.
   * Handles both filtering existing customers and adding the create option.
   *
   * @param options - List of all customer options
   * @param state - Current filter state including input value
   * @returns Filtered list of options, potentially including create option
   */
  const filterOptions = (
    options: SparseCustomer[],
    state: FilterOptionsState<SparseCustomer>
  ) => {
    // Apply base text filtering
    const filtered = filter(options, state);

    // Show only active customers when no search input
    if (state.inputValue === '') {
      return reject(filtered, 'isArchived');
    }

    // Check for exact name match (case-insensitive)
    const isMatch = filtered.some(({ name }) => {
      return name.toLowerCase() === state.inputValue.toLowerCase();
    });

    // Return filtered list if exact match found
    if (isMatch) {
      return filtered;
    }

    // Add create option if no exact match
    return [
      ...filtered,
      {
        id: CREATE_CUSTOMER_OPTION_ID,
        key: CREATE_CUSTOMER_OPTION_ID,
        name: state.inputValue,
        isArchived: false,
        isCustomer: true,
        isSupplier: false,
        isManufacturer: false,
      },
    ];
  };

  /**
   * Handles changes to the selected value.
   * Routes different types of selection to appropriate handlers.
   */
  const handleChange = (_: unknown, value: SparseCustomer | string | null) => {
    // Handle direct string input (create customer)
    if (typeof value === 'string') {
      return props.onCreate(value);
    }

    // Handle clearing selection
    if (value === null) {
      return props.onChange(null);
    }

    // Handle selecting create option
    if (value.id === CREATE_CUSTOMER_OPTION_ID) {
      return props.onCreate(value.name);
    }

    // Handle selecting existing customer
    props.onChange(value);
  };

  /**
   * Groups customers by supplier status based on groupByMode.
   * Returns undefined if no grouping is enabled.
   */
  const groupBy = props.groupByMode
    ? (option: SparseCustomer) => {
        if (option.id === CREATE_CUSTOMER_OPTION_ID) {
          return '';
        }
        return option.isSupplier ? 'Suppliers' : 'Customers';
      }
    : undefined;

  return (
    <CustomerAutocomplete
      freeSolo
      clearOnBlur
      forcePopupIcon={true}
      ref={ref}
      size="small"
      name={props.name}
      value={props.value}
      error={props.error}
      label={props.label}
      options={customers}
      loading={isLoading}
      onChange={handleChange}
      helperText={props.helperText}
      disableClearable={props.disableClearable}
      filterOptions={filterOptions}
      groupBy={groupBy}
      loadingText="Loading..."
      noOptionsText="No customers found"
    />
  );
}

/**
 * Forwarded ref version of the CustomerSearch component.
 * Allows parent components to access the underlying input element.
 */
export const CustomerSearch = forwardRef(CustomerSearchRenderer);
